<template>
    <Head :title="surveyDocument?.template.name ? `${surveyDocument.template.name} — Survey builder` : 'Survey builder'" />
    <div class="min-h-full bg-surface">
        <header class="border-b border-border shadow-sm bg-white px-4 sm:px-10 py-4 flex flex-wrap items-center justify-between gap-3 sticky top-0 z-10">
            <a :href="builder.adminUrl.value" class="body-small text-ink-muted hover:text-ink flex items-center gap-1">
                <i class="bi bi-chevron-left" aria-hidden="true"></i> Django admin
            </a>
            <div class="flex items-center gap-3">
                <AssessmentEyebrow>Survey builder</AssessmentEyebrow>
                <span v-if="builder.dirty.value" class="details text-ink-muted">Unsaved changes</span>
                <span v-else-if="savedAt" class="details text-ink-muted">Saved {{ formatDate(savedAt, "time") }}</span>
                <BaseButton theme="primary" size="sm" :disabled="builder.saving.value || !builder.dirty.value" @click="onSave">
                    {{ builder.saving.value ? "Saving…" : "Save survey" }}
                </BaseButton>
            </div>
        </header>

        <main class="max-w-[1600px] mx-auto px-4 sm:px-10 py-10 flex flex-col gap-6">
            <div v-if="builder.loading.value" class="body-small text-ink-muted">Loading…</div>
            <BaseNotification v-else-if="builder.loadError.value" theme="error" :text="builder.loadError.value" hide-dismiss />

            <template v-else-if="surveyDocument && choices">
                <BasePageTitle :title="surveyDocument.template.name || 'New survey'" />
                <BaseNotification v-if="builder.saveError.value" theme="error" :text="builder.saveError.value" hide-dismiss />
                <BaseNotification v-if="builder.cycleCount.value > 0" theme="info" :text="inUseWarning" hide-dismiss />

                <div class="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] items-start">
                    <div class="flex flex-col gap-6">
                        <SurveySettingsPanel :template="surveyDocument.template" :choices="choices" @update="builder.updateTemplate" />

                        <section class="flex flex-col gap-3">
                            <h2 class="title-section text-ink">Questions</h2>

                            <div ref="sectionList" :data-list-id="ROOT" class="flex flex-col gap-3">
                                <SurveySectionCard
                                    v-for="section in builder.topSections.value"
                                    :key="section.ref"
                                    :section="section"
                                    :builder="builder"
                                    :choices="choices"
                                    :default-scale-label="defaultScaleLabel"
                                    @focus-question="focusInPreview" />
                            </div>

                            <div v-if="unsectionedQuestions.length > 0" class="rounded-lgr border border-border bg-white p-4 flex flex-col gap-2">
                                <div>
                                    <div class="body-small-bold text-ink">Questions outside any section</div>
                                    <div class="details text-ink-muted">Shown after all sections, in this order. Never randomized, even when "Randomize question order" is on.</div>
                                </div>
                                <div ref="unsectionedList" :data-list-id="UNSECTIONED" class="flex flex-col gap-2">
                                    <SurveyQuestionRow
                                        v-for="(question, index) in unsectionedQuestions"
                                        :key="question.client_id"
                                        :question="question"
                                        :index="index"
                                        :question-types="choices.question_types"
                                        :scales="choices.scales"
                                        :strength-directions="choices.strength_directions"
                                        :default-scale-label="defaultScaleLabel"
                                        :locked="builder.hasResponses(question.id)"
                                        @focus="focusInPreview(question.client_id)"
                                        @update="builder.updateQuestion(question.client_id, $event)"
                                        @duplicate="builder.duplicateQuestion(question.client_id)"
                                        @remove="builder.removeQuestion(question.client_id)" />
                                </div>
                            </div>

                            <div class="flex items-center gap-4">
                                <BaseButton theme="neutral" size="sm" @click="builder.addSection()"><i class="bi bi-plus-lg" aria-hidden="true"></i> Add section</BaseButton>
                                <BaseButton theme="neutral" size="sm" @click="onAddLooseQuestion"><i class="bi bi-plus-lg" aria-hidden="true"></i> Add question</BaseButton>
                            </div>
                        </section>
                    </div>

                    <BaseCard padding="lg" as="section" class="xl:sticky xl:top-24">
                        <div class="flex items-center justify-between mb-4">
                            <h2 class="title-section text-ink">Participant preview</h2>
                            <span class="details text-ink-muted">{{ displayModeLabel }}</span>
                        </div>
                        <SurveyPreview v-if="previewDocument" ref="preview" :document="previewDocument" :choices="choices" />
                    </BaseCard>
                </div>
            </template>
        </main>
    </div>
</template>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import AssessmentEyebrow from "~vue/components/AssessmentEyebrow.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageTitle from "~vue/components/BasePageTitle.vue";
import { useSortableList } from "~vue/composables/useSortableList";
import SurveyPreview from "~vue/SurveyBuilder/SurveyPreview.vue";
import SurveyQuestionRow from "~vue/SurveyBuilder/SurveyQuestionRow.vue";
import SurveySectionCard from "~vue/SurveyBuilder/SurveySectionCard.vue";
import SurveySettingsPanel from "~vue/SurveyBuilder/SurveySettingsPanel.vue";
import { ROOT, UNSECTIONED, useSurveyBuilder } from "~vue/SurveyBuilder/useSurveyBuilder";
import { formatDate } from "~vue/utils/formatDate";
import { computed, onMounted, ref, useTemplateRef } from "vue";

const props = defineProps<{ load_url: string; save_url: string }>();

const builder = useSurveyBuilder(props.load_url, props.save_url);
const surveyDocument = builder.document;
const choices = builder.choices;
const previewDocument = builder.orderedDocument;

const sectionList = useTemplateRef<HTMLElement>("sectionList");
const unsectionedList = useTemplateRef<HTMLElement>("unsectionedList");
const preview = ref<InstanceType<typeof SurveyPreview> | null>(null);
const savedAt = ref<Date | null>(null);

useSortableList(sectionList, { group: "survey-sections", handle: ".section-handle", onMove: builder.applySectionMove });
useSortableList(unsectionedList, { group: "survey-questions", handle: ".question-handle", onMove: builder.applyQuestionMove });

const unsectionedQuestions = computed(() => builder.questionsOf(null));

const defaultScaleLabel = computed(() => choices.value?.scales.find((scale) => scale.value === surveyDocument.value?.template.default_scale)?.label ?? "default scale");

const displayModeLabel = computed(() => choices.value?.display_modes.find((mode) => mode.value === surveyDocument.value?.template.display_mode)?.label ?? "");

const inUseWarning = computed(() => {
    const count = builder.cycleCount.value;
    return `${count} assessment${count === 1 ? " has" : "s have"} already run on this survey — edits change what future raters see, and answered questions can't be deleted.`;
});

function focusInPreview(clientId: string) {
    preview.value?.focusQuestion(clientId);
}

function onAddLooseQuestion() {
    const question = builder.addQuestion(null);
    if (question) {
        focusInPreview(question.client_id);
    }
}

async function onSave() {
    const saved = await builder.save();
    if (!saved) {
        return;
    }
    savedAt.value = new Date();
    // A survey created from the blank builder now has its own URL. The save
    // response already carries its save/builder endpoints, so rewrite the
    // address bar in place rather than navigating: an Inertia visit here would
    // remount the builder mid-edit and discard anything typed since the save.
    if (builder.builderUrl.value && builder.builderUrl.value !== globalThis.location.pathname) {
        globalThis.history.replaceState(globalThis.history.state, "", builder.builderUrl.value);
    }
}

onMounted(builder.load);
</script>
